import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import { assertNever } from '@kofno/piper';
import GroupSuccessModal from '../Shared/GroupSuccessModal';
import { T } from '../../../../Translations';
import { newGroupStore } from '../../../../NewGroupStore';

/**
 * Post-create confirmation modal for the Add Group workflow. Mirrors
 * ParticipantAdded's structure so the two workflows read the same.
 */
function GroupAdded() {
  const navigate = useNavigate();

  const addAnother = () => {
    newGroupStore.reset();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const returnToList = () => {
    newGroupStore.reset();
    navigate('/people/groups');
  };

  const state = newGroupStore.state;
  switch (state.kind) {
    case 'waiting':
    case 'loading':
    case 'load-error':
    case 'ready':
    case 'submitting':
    case 'submit-error':
      return <></>;

    case 'success': {
      const licenseCount = newGroupStore.selectedLicenseIds.length;
      const collectionCount = newGroupStore.selectedCollectionIds.length;
      // Navigating unmounts the page, whose cleanup resets the store.
      const addParticipants = () => navigate(`/people/groups/${state.createdGroup.payload.id}`);

      return (
        <GroupSuccessModal
          heading={<T kind="Group Created" />}
          name={newGroupStore.name}
          licenseCount={licenseCount}
          collectionCount={collectionCount}
          onClose={returnToList}
        >
          <button
            className="w-full py-2.5 px-4 rounded-lg bg-accent text-accent-foreground hover:opacity-90 transition-opacity"
            style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)' }}
            onClick={addParticipants}
            data-intercom-target="confirmation-add-participants-to-group"
          >
            <T kind="Add Participants to this Group" />
          </button>
          <button
            className="w-full py-2.5 px-4 rounded-lg border-2 border-primary text-primary bg-transparent hover:opacity-90 transition-opacity"
            style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)' }}
            onClick={addAnother}
            data-intercom-target="confirmation-add-another-group"
          >
            <T kind="Add Another Group" />
          </button>
          <button
            className="w-full py-2 text-muted-foreground hover:text-foreground transition-colors"
            style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-medium)' }}
            onClick={returnToList}
            data-intercom-target="confirmation-return-to-groups-list"
          >
            <T kind="Return to Groups List" />
          </button>
        </GroupSuccessModal>
      );
    }

    default:
      return assertNever(state);
  }
}

export default observer(GroupAdded);
